import type { Entry } from '@ozone/shared';
import { CATEGORY_ORDER, currentMonthKey } from './compose';

/**
 * AOZONE-33 admin-ui filter logic. Pure functions backing the FilterBar:
 * category/submitter multiselects, month picker, free-text search, plus
 * URL serialization so filter state survives reloads / link-sharing.
 *
 * A `Selection` is either the literal `'all'` (no restriction) or an explicit
 * array of values. An empty array means "nothing selected" → matches nothing.
 */
export type Selection = 'all' | string[];

export interface FilterState {
  categories: Selection;
  submitters: Selection;
  month: string;
  search: string;
  /**
   * AOZONE-91 own/all toggle for the contributor TeamView. `undefined`/`true`
   * mean "own entries only" (the default); `false` means "show all". The
   * own/all split itself is done client-side in TeamView (applyFilters can't
   * know the caller's slackUserId). Only the admin UI leaves this untouched, so
   * it is optional and never affects admin URLs.
   */
  mine?: boolean;
}

export interface MonthOption {
  key: string;
  label: string;
}

export function defaultFilterState(now?: Date): FilterState {
  return {
    categories: 'all',
    submitters: 'all',
    month: currentMonthKey(now),
    search: '',
  };
}

export function distinctSubmitters(
  entries: Entry[],
): Array<{ submitterId: string; submitterAlias: string }> {
  const seen = new Map<string, string>();
  for (const e of entries) {
    if (!seen.has(e.submitterId)) {
      seen.set(e.submitterId, e.submitterAlias);
    }
  }
  return [...seen.entries()]
    .map(([submitterId, submitterAlias]) => ({ submitterId, submitterAlias }))
    .sort((a, b) =>
      a.submitterAlias.toLowerCase().localeCompare(b.submitterAlias.toLowerCase()),
    );
}

function monthKeyFromParts(year: number, monthIndex: number): string {
  const d = new Date(Date.UTC(year, monthIndex, 1));
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthLabelFromParts(year: number, monthIndex: number): string {
  const d = new Date(Date.UTC(year, monthIndex, 1));
  return d.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function monthOptions(now: Date = new Date()): MonthOption[] {
  const year = now.getUTCFullYear();
  const monthIndex = now.getUTCMonth();
  const opts: MonthOption[] = [];
  for (let i = 0; i < 7; i++) {
    opts.push({
      key: monthKeyFromParts(year, monthIndex - i),
      label: monthLabelFromParts(year, monthIndex - i),
    });
  }
  return opts;
}

/**
 * AOZONE-60 — return the YYYY-MM canonical period for the calendar month
 * preceding `period`. Mirrors `previousPeriod` in admin-api/src/fiscal.ts.
 */
export function previousPeriod(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 2, 15));
  const y = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${mm}`;
}

/**
 * AOZONE-60 — "December FY27"-style label for a YYYY-MM period. Mirrors
 * `fiscalMonthLabelForPeriod` in admin-api/src/fiscal.ts.
 */
export function fiscalLabelFor(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const year = Number(m[1]);
  const monthIdx = Number(m[2]) - 1;
  const d = new Date(Date.UTC(year, monthIdx, 15));
  const monthName = d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  // Qumulo FY: starts Feb 1, one year ahead. Equivalent to FY = year + (month >= Feb ? 1 : 0).
  const fyNum = (year + (monthIdx >= 1 ? 1 : 0)) % 100;
  return `${monthName} FY${fyNum}`;
}

/** Human label ("May 2026") for a `YYYY-MM` key, using UTC. */
export function monthLabelFor(monthKey: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!m) return monthKey;
  return monthLabelFromParts(Number(m[1]), Number(m[2]) - 1);
}

/**
 * Free-text search, shared by the admin view and the contributor TeamView (both
 * reach it through `applyFilters`).
 *
 * THE RULE — free-text search reads exactly the fields the views render as
 * entry-identifying text. That is the boundary; it has a fixed referent, so it
 * does not ratchet outward one field per bug report. Judge any future addition
 * against the rule, not against the length of the array below.
 *
 *   searched: customerName, partnerOrCloud, target, title, narrativeBody,
 *             actionItem
 *   excluded: submitterAlias — the submitter multiselect owns that dimension,
 *               and folding an alias substring into free-text would silently
 *               widen every query;
 *             opaque ids (submitterId, entryId) — not text a human reads;
 *             admin-curation / draft fields (includeInMbr, mbrDraftMd,
 *               mbrDraft*) — not entry-identifying, and contributors must not
 *               be able to probe curation state through search.
 *
 * The exclusion list above is exhaustive as of this writing. Keep it that way:
 * a list that merely looks complete is worse than no list, because a reader who
 * checks the boundary, sees a complete-looking enumeration, and stops looking
 * has been actively misled.
 *
 * History. AOZONE-114 reported that contributors could not find their own
 * action items; the field was rendered nowhere in the contributor view and
 * searched nowhere in either view. The ticket's AC3 named only "action item
 * text and title", but `customerName ?? partnerOrCloud ?? target` is what the
 * views actually render as the identifier — the contributor "Customer /
 * Partner" column, and `entryHeadline` (compose.ts) for the admin row's bold
 * headline. So a BD Activities entry's visible "AirWalk Reply" and an
 * Enablement entry's `target` had the identical bug, and fixing only two of the
 * four would have been arbitrary. AC3 was therefore WIDENED DELIBERATELY, by
 * weave (product owner), on 2026-08-04 — code intentionally exceeds the written
 * AC here; it is not an overreach.
 */
export function matchesSearch(entry: Entry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  return [
    entry.customerName,
    entry.partnerOrCloud,
    entry.target,
    entry.title,
    entry.narrativeBody,
    entry.actionItem,
  ].some((field) => (field ?? '').toLowerCase().includes(q));
}

export function applyFilters(entries: Entry[], state: FilterState): Entry[] {
  return entries.filter((e) => {
    if ((e.mbrPeriod ?? '') !== state.month) return false;

    if (state.categories !== 'all') {
      if (e.entryCategory == null || !state.categories.includes(e.entryCategory)) {
        return false;
      }
    }

    if (state.submitters !== 'all') {
      if (!state.submitters.includes(e.submitterId)) return false;
    }

    return matchesSearch(e, state.search);
  });
}

function isFullCategoryUniverse(arr: string[]): boolean {
  if (arr.length !== CATEGORY_ORDER.length) return false;
  return CATEGORY_ORDER.every((c) => arr.includes(c));
}

export function serializeFilters(state: FilterState, now?: Date): string {
  const parts: string[] = [];

  // Values are URL-encoded with encodeURIComponent (spaces → %20). Category
  // names can contain spaces/commas-in-theory, so we encode the whole joined
  // string (comma → %2C). Submitter ids are opaque tokens with no special
  // chars, so we join their encoded tokens with a literal, readable comma.
  // Either way parseFilters splits on ',' after decoding, so both round-trip.
  if (state.categories !== 'all' && !isFullCategoryUniverse(state.categories)) {
    const joined = encodeURIComponent([...state.categories].sort().join(','));
    parts.push(`cat=${joined}`);
  }

  if (state.submitters !== 'all') {
    const joined = [...state.submitters].sort().map(encodeURIComponent).join(',');
    parts.push(`sub=${joined}`);
  }

  if (state.month !== currentMonthKey(now)) {
    parts.push(`month=${encodeURIComponent(state.month)}`);
  }

  if (state.search !== '') {
    parts.push(`q=${encodeURIComponent(state.search)}`);
  }

  // AOZONE-91: the own/all toggle. Default (own) is unserialized; only the
  // flipped "show all" state is emitted, mirroring the other filters.
  if (state.mine === false) {
    parts.push('mine=all');
  }

  return parts.join('&');
}

export function parseFilters(
  search: string,
  opts?: { now?: Date; allCategories?: readonly string[] },
): FilterState {
  const allCategories = opts?.allCategories ?? CATEGORY_ORDER;
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);

  let categories: Selection = 'all';
  const cat = params.get('cat');
  if (cat != null) {
    const tokens = cat
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '' && allCategories.includes(t));
    if (
      tokens.length === 0 ||
      (tokens.length === allCategories.length &&
        allCategories.every((c) => tokens.includes(c)))
    ) {
      categories = 'all';
    } else {
      categories = [...tokens].sort();
    }
  }

  let submitters: Selection = 'all';
  const sub = params.get('sub');
  if (sub != null) {
    submitters = sub
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '')
      .sort();
  }

  const monthParam = params.get('month');
  const month =
    monthParam != null && /^\d{4}-\d{2}$/.test(monthParam)
      ? monthParam
      : currentMonthKey(opts?.now);

  const search_ = params.get('q') ?? '';

  const result: FilterState = { categories, submitters, month, search: search_ };
  // AOZONE-91: only surface `mine` when the show-all toggle is active, so the
  // default (own) state parses identically to how the admin UI has always
  // parsed — no `mine` key at all.
  if (params.get('mine') === 'all') result.mine = false;
  return result;
}

export function isDefaultFilterState(state: FilterState, now?: Date): boolean {
  return (
    state.categories === 'all' &&
    state.submitters === 'all' &&
    state.month === currentMonthKey(now) &&
    state.search === ''
  );
}

export function selectionCount(
  sel: Selection,
  total: number,
): { selected: number; total: number } {
  if (sel === 'all') return { selected: total, total };
  return { selected: sel.length, total };
}
